import { useQuery } from '@tanstack/react-query';
import { fetchMyAccess } from '../api/resources';
import { useAuth } from '../context/AuthContext';
import { hasPortalGroup } from '../utils/roles';

/**
 * What the person signed in right now may do on an office screen.
 *
 * Office logins — admin, staff, viewer — may do everything their role allows,
 * as before. Every login the server puts in an account group is held to that
 * group's switches instead: workers, supervisors, and the two manager desks.
 * Screens built for the office ask here before offering a button that would
 * only come back refused.
 */
export function usePortalAccess() {
  const { role, user, companyId } = useAuth();
  const isPortal = hasPortalGroup(role);
  const { data } = useQuery({
    // Keyed by who is asking. What one person may do must never be handed
    // to the next person to sign in on this browser — that is how a worker
    // came to see a supervisor's menu.
    queryKey: ['my-access', companyId ?? null, user?.id ?? null],
    queryFn: () => fetchMyAccess(companyId),
    enabled: isPortal && !!companyId,
    staleTime: 60_000,
  });
  const access = data?.access || {};
  return {
    isPortal,
    isSupervisor: !!data?.isSupervisor,
    /** `can('createOrder')` — true for office logins, switched for grouped ones. */
    can: (feature) => !isPortal || !!access[feature],
  };
}
